import { useCreateExclusionMutation } from 'app/apis/commandApi';
import NavLinkButton from 'common/components/anchors/navlink-button';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import DropDownButtonLegacy from 'common/components/drop-downs/legacy/dropDownButtonLegacy';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import SimpleButtonContainerContent from 'common/components/drop-downs/legacy/simpleDropDownButtonContainerContent';
import Textbox from 'common/components/inputs/textbox';
import {
  ExclusionCategory,
  ExclusionLevel,
  ExclusionMatchTarget,
  ExclusionTypeLabel,
  RuleMatchType,
  typeMatchTargetMap
} from 'features/exclusions/exclusions.enums';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { onSuccess } from 'features/toast/toast.slice';
import { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'app/store';

const matchValueMinLength = 1;
const matchValueMaxLength = 1000;
const validateMatchValue = (matchValue: string): string[] => {
  if (matchValue.length < matchValueMinLength) {
    return [`Match value must be at least ${matchValueMinLength} character`];
  }

  if (matchValue.length > matchValueMaxLength) {
    return [`Match value must be less than ${matchValueMaxLength} characters`];
  }

  return [];
};

const additionalInformationMinLength = 1;
const additionalInformationMaxLength = 1000;
const validateAdditionalInformation = (
  additionalInformation: string
): string[] => {
  if (additionalInformation.length < additionalInformationMinLength) {
    return [
      `Additional Information must be at least ${additionalInformationMinLength} character`
    ];
  }

  if (additionalInformation.length > additionalInformationMaxLength) {
    return [
      `Additional Information must be less than ${additionalInformationMaxLength} characters`
    ];
  }

  return [];
};

export const SettingsExclusionsCreatePage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const [matchValue, onMatchValueChanged] = useState('');
  const [matchValueErrors, setMatchValueErrors] = useState(new Array<string>());
  const [additionalInformation, onAdditionalInformationChanged] = useState('');
  const [additionalInformationErrors, setAdditionalInformationErrors] =
    useState(new Array<string>());
  const [selectedExclusionCategory, onSelectedExclusionCategoryChanged] =
    useState(ExclusionCategory.Block);
  const allExclusionCategories = useMemo(
    () =>
      Object.values(ExclusionCategory).map<DropDownItemType>(
        (exclusionCategory) => {
          return {
            key: exclusionCategory,
            label: exclusionCategory,
            isActive: selectedExclusionCategory === exclusionCategory
          };
        }
      ),
    [selectedExclusionCategory]
  );

  const [selectedExclusionType, onSelectedExclusionTypeChanged] = useState(
    ExclusionTypeLabel.IpAddress
  );
  const allExclusionTypes = useMemo(
    () =>
      Object.values(ExclusionTypeLabel).map<DropDownItemType>(
        (exclusionType) => {
          return {
            key: exclusionType,
            label: exclusionType,
            isActive: selectedExclusionType === exclusionType
          };
        }
      ),
    [selectedExclusionType]
  );

  const [selectedExclusionMatchTarget, onSelectedExclusionMatchTargetChanged] =
    useState(ExclusionMatchTarget.RemoteAddress);
  const allExclusionMatchTargets = useMemo(
    () =>
      Object.values(ExclusionMatchTarget).map<DropDownItemType>(
        (exclusionMatchTarget) => {
          return {
            key: exclusionMatchTarget,
            label: exclusionMatchTarget,
            isActive: selectedExclusionMatchTarget === exclusionMatchTarget
          };
        }
      ),
    [selectedExclusionMatchTarget]
  );

  const [selectedRuleMatchType, onSelectedRuleMatchTypeChanged] = useState(
    RuleMatchType.Equals
  );
  const allRuleMatchTypes = useMemo(
    () =>
      Object.values(RuleMatchType).map<DropDownItemType>((ruleMatchType) => {
        return {
          key: ruleMatchType,
          label: ruleMatchType,
          isActive: selectedRuleMatchType === ruleMatchType
        };
      }),
    [selectedRuleMatchType]
  );

  const [createExclusion, { isLoading }] = useCreateExclusionMutation();

  const onCreateExclusion = () => {
    void createExclusion({
      siteId,
      exclusionLevel: ExclusionLevel.SiteSpecificManual,
      exclusionCategory: selectedExclusionCategory,
      exclusionType: selectedExclusionType,
      exclusionMatchTarget:
        selectedExclusionCategory === ExclusionCategory.Block
          ? typeMatchTargetMap[selectedExclusionType]
          : selectedExclusionMatchTarget,
      additionalInformation,
      ruleMatchType: selectedRuleMatchType,
      value: matchValue
    })
      .unwrap()
      .then(() => {
        dispatch(
          onSuccess({
            title: 'Exclusion Created',
            description: 'The exclusion has successfully been created!'
          })
        );
        navigate('/settings/exclusions');
      })
      .catch();
  };

  return (
    <>
      <TileGridContainer>
        <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
          <section className="mx-6 my-6">
            <h2 className="text-primary-700 text-l leading-7 pb-2 mb-3 mt-2 border-b border-neutral-200">
              New Exclusion
            </h2>

            <div className="lg:w-144 mt-4">
              <div className="lg:w-144 mt-2">
                <label className="block mb-1 text-sm">Category</label>
                <DropDownButtonLegacy
                  dropDownItems={allExclusionCategories}
                  selectDropDownItem={(exclusionCategory) => {
                    onSelectedExclusionCategoryChanged(
                      exclusionCategory as ExclusionCategory
                    );
                  }}
                  dropDirection={'left'}
                >
                  <SimpleButtonContainerContent
                    dropdownLabel={selectedExclusionCategory}
                    additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
                  />
                </DropDownButtonLegacy>
              </div>

              {selectedExclusionCategory === ExclusionCategory.Ignore ? (
                <div className="lg:w-144 mt-2">
                  <label className="block mb-1 text-sm">Match Target</label>
                  <DropDownButtonLegacy
                    dropDownItems={allExclusionMatchTargets}
                    selectDropDownItem={(exclusionMatchTarget) => {
                      onSelectedExclusionMatchTargetChanged(
                        exclusionMatchTarget as ExclusionMatchTarget
                      );
                    }}
                    dropDirection={'left'}
                  >
                    <SimpleButtonContainerContent
                      dropdownLabel={selectedExclusionMatchTarget}
                      additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
                    />
                  </DropDownButtonLegacy>
                </div>
              ) : (
                <></>
              )}

              <div className="lg:w-144 mt-2">
                <label className="block mb-1 text-sm">Type</label>
                <DropDownButtonLegacy
                  dropDownItems={allExclusionTypes}
                  selectDropDownItem={(exclusionType) => {
                    onSelectedExclusionTypeChanged(
                      exclusionType as ExclusionTypeLabel
                    );
                  }}
                  dropDirection={'left'}
                >
                  <SimpleButtonContainerContent
                    dropdownLabel={selectedExclusionType}
                    additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
                  />
                </DropDownButtonLegacy>
              </div>

              <div className="lg:w-144 mt-2">
                <label className="block mb-1 text-sm">Rule Match Type</label>
                <DropDownButtonLegacy
                  dropDownItems={allRuleMatchTypes}
                  selectDropDownItem={(ruleMatchType) => {
                    onSelectedRuleMatchTypeChanged(
                      ruleMatchType as RuleMatchType
                    );
                  }}
                  dropDirection={'left'}
                >
                  <SimpleButtonContainerContent
                    dropdownLabel={selectedRuleMatchType}
                    additionalContainerStyling="bg-white rounded border border-neutral-200 w-40"
                  />
                </DropDownButtonLegacy>
              </div>

              <div className="lg:w-144 mt-2">
                <label
                  htmlFor="exclusion-match-value"
                  className="block mb-1 text-sm"
                >
                  Match Value
                </label>
                <Textbox
                  identifier="exclusion-match-value"
                  value={matchValue}
                  onChange={(value) => {
                    const errors = validateMatchValue(value);
                    setMatchValueErrors(errors);
                    onMatchValueChanged(value);
                  }}
                  errors={matchValueErrors}
                  maxCharacterLimit={matchValueMaxLength}
                />
              </div>

              <div className="lg:w-144 mt-2">
                <label
                  htmlFor="exclusion-additional-information"
                  className="block mb-1 text-sm"
                >
                  Additional Information
                </label>
                <Textbox
                  identifier="exclusion-additional-information"
                  value={additionalInformation}
                  onChange={(value) => {
                    const errors = validateAdditionalInformation(value);
                    setAdditionalInformationErrors(errors);
                    onAdditionalInformationChanged(value);
                  }}
                  errors={additionalInformationErrors}
                  maxCharacterLimit={additionalInformationMaxLength}
                />
              </div>
            </div>
            <div className="mt-6 flex items-center">
              <Button
                theme={ButtonTheme.PRIMARY}
                identifier="create-exclusion"
                onClick={onCreateExclusion}
                isLoading={isLoading}
              >
                <span>Create Exclusion</span>
              </Button>

              <div className="ml-4">
                <NavLinkButton
                  theme={ButtonTheme.CANCEL}
                  identifier="cancel-create-exclusion"
                  to="/settings/exclusions"
                  classOverrides="px-4 py-2.5 block"
                >
                  <span>Cancel</span>
                </NavLinkButton>
              </div>
            </div>
          </section>
        </TileWithoutDefaultStyling>
      </TileGridContainer>
    </>
  );
};
